import {
  FilesetResolver,
  ImageSegmenter,
  type ImageSegmenterResult,
} from "@mediapipe/tasks-vision";
import type { FxQualityTier } from "./fxRuntime";
import { getFxSegmentationTierTuning } from "./fxSegmentationTuning";

export type SegmentationRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type SegmentationMaskState = {
  width: number;
  height: number;
  alpha: Float32Array;
  meanConfidence: number;
  coverage: number;
  scale: number;
  generatedAtMs: number;
};

export type BodySegmentationState = {
  elementId: string;
  personRect: SegmentationRect | null;
  mask: SegmentationMaskState | null;
  confidence: number;
  consecutiveMisses: number;
  fallback: boolean;
  lastUpdatedMs: number;
};

type InternalBodySegmentationState = BodySegmentationState & {
  missCount: number;
};

type TickArgs = {
  videoMap: Map<string, HTMLVideoElement>;
  targetElementIds: string[];
  qualityTier: FxQualityTier;
  force?: boolean;
  nowMs?: number;
};

type SegmentationInputBundle = {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
};

const MEDIAPIPE_WASM_URL = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm";
// MediaPipe Image Segmenter docs list SelfieSegmenter as person/background model
// (background index 0, person index 1) for background replacement/blur use-cases.
const SEGMENTER_MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite";
const PERSON_LABEL = "person";
const MIN_CONFIDENCE = 0.12;
const DETECTOR_FAIL_COOLDOWN_MS = 2500;
const FREEZE_MISS_LIMIT = 8;
const SEARCH_MISS_LIMIT = 24;
const RECT_SMOOTHING_ALPHA = 0.28;
const DEFAULT_DETECT_INTERVAL_MS = 140;
const DEFAULT_SAMPLE_STRIDE = 1;
const DEFAULT_FRAME_STRIDE = 1;
const DEFAULT_MASK_SCALE = 0.75;
const DEFAULT_MASK_EMA_ALPHA = 0.45;
const CONFIDENCE_MASK_ALPHA_THRESHOLD = 0.55;
const CONFIDENCE_MASK_ALPHA_SOFTNESS = 0.22;
const SEGMENTATION_INPUT_LONG_EDGE_BY_TIER: Record<FxQualityTier, number> = {
  draft: 256,
  balanced: 384,
  final: 512,
};

export class FxBodySegmenter {
  private states = new Map<string, InternalBodySegmentationState>();
  private segmenter: ImageSegmenter | null = null;
  private segmenterPromise: Promise<ImageSegmenter> | null = null;
  private detectRunning = false;
  private disabledUntilMs = 0;
  private lastDetectMs = 0;
  private lastTickMs = -1;
  private frameCounter = 0;
  private roundRobinCursor = 0;
  private personLabelIndex: number | null = null;
  private inferredPersonMaskIndex: number | null = null;
  private inferredPersonCategoryIndex: number | null = null;
  private inputBundleByTier = new Map<FxQualityTier, SegmentationInputBundle>();

  tick(args: TickArgs): void {
    const { videoMap, qualityTier } = args;
    const nowMs = args.nowMs ?? now();
    const forceTick = args.force === true;
    if (this.lastTickMs >= 0 && Math.abs(nowMs - this.lastTickMs) < 0.5) return;
    this.lastTickMs = nowMs;
    const tuning = resolveTierTuning(qualityTier);
    const targets = dedupe(args.targetElementIds);
    const targetSet = new Set(targets);

    this.pruneInactiveTargets(targetSet);
    if (targets.length === 0) return;
    if (this.detectRunning) return;
    if (nowMs < this.disabledUntilMs) return;
    this.frameCounter += 1;
    if (!forceTick) {
      if (tuning.frameStride > 1 && this.frameCounter % tuning.frameStride !== 0) return;
      if (nowMs - this.lastDetectMs < tuning.detectIntervalMs) return;
    }

    const candidate = this.pickNextCandidate(targets, videoMap);
    if (!candidate) {
      for (const id of targets) this.applyMiss(id, nowMs);
      return;
    }

    this.lastDetectMs = nowMs;
    this.detectRunning = true;
    void this
      .sampleCandidate(candidate.elementId, candidate.video, tuning, qualityTier, nowMs)
      .catch(() => undefined)
      .finally(() => {
        this.detectRunning = false;
      });
  }

  getState(elementId: string): BodySegmentationState | null {
    const s = this.states.get(elementId);
    if (!s) return null;
    return {
      elementId: s.elementId,
      personRect: s.personRect ? { ...s.personRect } : null,
      mask: s.mask
        ? {
            ...s.mask,
            alpha: s.mask.alpha,
          }
        : null,
      confidence: s.confidence,
      consecutiveMisses: s.missCount,
      fallback: s.fallback,
      lastUpdatedMs: s.lastUpdatedMs,
    };
  }

  reset(): void {
    this.states.clear();
    this.roundRobinCursor = 0;
    this.lastTickMs = -1;
    this.frameCounter = 0;
    this.inferredPersonMaskIndex = null;
    this.inferredPersonCategoryIndex = null;
    this.inputBundleByTier.clear();
  }

  dispose(): void {
    this.reset();
    if (this.segmenter) {
      try {
        this.segmenter.close();
      } catch {}
    }
    this.segmenter = null;
    this.segmenterPromise = null;
    this.personLabelIndex = null;
    this.inferredPersonMaskIndex = null;
    this.inferredPersonCategoryIndex = null;
    this.detectRunning = false;
    this.disabledUntilMs = 0;
  }

  private async sampleCandidate(
    elementId: string,
    video: HTMLVideoElement,
    tuning: ResolvedTierTuning,
    qualityTier: FxQualityTier,
    nowMs: number
  ): Promise<void> {
    let result: ImageSegmenterResult | null = null;
    try {
      const segmenter = await this.ensureSegmenter();
      const segmentationInput = this.resolveSegmentationInput(video, qualityTier);
      result = segmenter.segmentForVideo(segmentationInput, nowMs);
      const prevMask = this.states.get(elementId)?.mask ?? null;
      const personSegmentation = this.extractPersonSegmentation(result, {
        nowMs,
        sampleStride: tuning.sampleStride,
        maskScale: tuning.maskScale,
        maskEmaAlpha: tuning.maskEmaAlpha,
        previousMask: prevMask,
      });

      if (!personSegmentation || personSegmentation.confidence < MIN_CONFIDENCE) {
        this.applyMiss(elementId, nowMs);
        return;
      }

      const normalized = personSegmentation.rect;
      const previous = this.states.get(elementId)?.personRect;
      const nextRect = previous
        ? {
            x: lerp(previous.x, normalized.x, RECT_SMOOTHING_ALPHA),
            y: lerp(previous.y, normalized.y, RECT_SMOOTHING_ALPHA),
            width: lerp(previous.width, normalized.width, RECT_SMOOTHING_ALPHA),
            height: lerp(previous.height, normalized.height, RECT_SMOOTHING_ALPHA),
          }
        : normalized;

      this.states.set(elementId, {
        elementId,
        personRect: nextRect,
        mask: personSegmentation.mask,
        confidence: personSegmentation.confidence,
        consecutiveMisses: 0,
        fallback: false,
        lastUpdatedMs: nowMs,
        missCount: 0,
      });
    } catch {
      this.disabledUntilMs = nowMs + DETECTOR_FAIL_COOLDOWN_MS;
      this.applyMiss(elementId, nowMs);
    } finally {
      disposeImageSegmenterResult(result);
    }
  }

  private async ensureSegmenter(): Promise<ImageSegmenter> {
    if (this.segmenter) return this.segmenter;
    if (this.segmenterPromise) return this.segmenterPromise;

    this.segmenterPromise = (async () => {
      const vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_URL);
      const delegates: Array<"GPU" | "CPU"> = ["GPU", "CPU"];
      let lastError: unknown = null;

      for (const delegate of delegates) {
        try {
          const instance = await ImageSegmenter.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath: SEGMENTER_MODEL_URL,
              delegate,
            },
            runningMode: "VIDEO",
            outputCategoryMask: true,
            outputConfidenceMasks: true,
          });
          this.segmenter = instance;
          this.personLabelIndex = this.resolvePersonLabelIndex(instance.getLabels());
          return instance;
        } catch (error) {
          lastError = error;
        }
      }

      throw (lastError instanceof Error ? lastError : new Error("Failed to initialize ImageSegmenter delegates."));
    })();

    try {
      return await this.segmenterPromise;
    } catch (error) {
      this.segmenterPromise = null;
      throw error;
    }
  }

  private resolvePersonLabelIndex(labels: string[]): number | null {
    if (!Array.isArray(labels) || labels.length === 0) return null;
    const index = labels.findIndex((label) => label.trim().toLowerCase() === PERSON_LABEL);
    return index >= 0 ? index : null;
  }

  private extractPersonSegmentation(
    result: ImageSegmenterResult,
    opts: {
      nowMs: number;
      sampleStride: number;
      maskScale: number;
      maskEmaAlpha: number;
      previousMask: SegmentationMaskState | null;
    }
  ): { rect: SegmentationRect; confidence: number; mask: SegmentationMaskState } | null {
    const personIndex = this.personLabelIndex;
    const stride = clampInt(opts.sampleStride, 1, 4, DEFAULT_SAMPLE_STRIDE);
    const confidenceMasks = Array.isArray(result.confidenceMasks) ? result.confidenceMasks : [];
    const resolvedPersonIndex = this.resolveRuntimePersonMaskIndex({
      personLabelIndex: personIndex,
      confidenceMasks,
      stride,
    });
    const candidates: Array<{
      source: "category" | "confidence";
      rect: SegmentationRect;
      confidence: number;
      mask: SegmentationMaskState;
    }> = [];

    // Preferred path: category mask (discrete class index) is usually more stable
    // than raw confidence maps for binary foreground extraction.
    if (result.categoryMask) {
      const mask = result.categoryMask;
      const data = mask.getAsUint8Array();
      const categoryPersonIndex = this.resolveCategoryPersonIndex({
        personLabelIndex: personIndex,
        runtimePersonIndex: resolvedPersonIndex,
        categoryData: data,
      });
      if (categoryPersonIndex == null) return null;
      const rect = computeRectFromCategoryMask({
        data,
        width: mask.width,
        height: mask.height,
        personIndex: categoryPersonIndex,
        stride,
      });
      if (!rect) return null;
      const builtMask = buildMaskFromCategoryMask({
        data,
        width: mask.width,
        height: mask.height,
        personIndex: categoryPersonIndex,
        sampleStride: stride,
        maskScale: opts.maskScale,
        emaAlpha: opts.maskEmaAlpha,
        previousMask: opts.previousMask,
        generatedAtMs: opts.nowMs,
      });
      if (builtMask.coverage >= 0.003 && builtMask.coverage <= 0.985) {
        candidates.push({
          source: "category",
          rect: rect.rect,
          confidence: rect.confidence,
          mask: builtMask,
        });
      }
    }

    // Fallback path: confidence mask for resolved person class.
    if (resolvedPersonIndex != null && confidenceMasks[resolvedPersonIndex]) {
      const mask = confidenceMasks[resolvedPersonIndex];
      const data = mask.getAsFloat32Array();
      const rect = computeRectFromConfidenceMask({
        data,
        width: mask.width,
        height: mask.height,
        threshold: 0.45,
        stride,
      });
      if (!rect) return null;
      const builtMask = buildMaskFromConfidenceMask({
        data,
        width: mask.width,
        height: mask.height,
        alphaThreshold: CONFIDENCE_MASK_ALPHA_THRESHOLD,
        alphaSoftness: CONFIDENCE_MASK_ALPHA_SOFTNESS,
        sampleStride: stride,
        maskScale: opts.maskScale,
        emaAlpha: opts.maskEmaAlpha,
        previousMask: opts.previousMask,
        generatedAtMs: opts.nowMs,
      });
      if (builtMask.coverage >= 0.003 && builtMask.coverage <= 0.985) {
        candidates.push({
          source: "confidence",
          rect: rect.rect,
          confidence: rect.confidence,
          mask: builtMask,
        });
      }
    }

    if (candidates.length === 0) return null;
    if (candidates.length === 1) {
      const winner = candidates[0];
      return {
        rect: winner.rect,
        confidence: winner.confidence,
        mask: winner.mask,
      };
    }
    const preferred = selectPreferredSegmentationCandidate({
      candidates,
      previousCoverage: opts.previousMask?.coverage ?? null,
    });
    return {
      rect: preferred.rect,
      confidence: preferred.confidence,
      mask: preferred.mask,
    };
  }

  private resolveCategoryPersonIndex(args: {
    personLabelIndex: number | null;
    runtimePersonIndex: number | null;
    categoryData: Uint8Array;
  }): number | null {
    const { personLabelIndex, runtimePersonIndex, categoryData } = args;
    if (personLabelIndex != null) return personLabelIndex;
    if (this.inferredPersonCategoryIndex != null && hasCategoryValue(categoryData, this.inferredPersonCategoryIndex)) {
      return this.inferredPersonCategoryIndex;
    }

    const preferredCandidates = dedupeNumbers([
      runtimePersonIndex,
      1,   // common person index in Selfie Segmenter metadata.
      255, // some category masks emit foreground as 255.
    ]).filter((candidate): candidate is number => Number.isFinite(candidate) && candidate > 0);
    for (const candidate of preferredCandidates) {
      if (hasCategoryValue(categoryData, candidate)) {
        this.inferredPersonCategoryIndex = candidate;
        return candidate;
      }
    }

    const inferred = inferCategoryForegroundIndex(categoryData);
    if (inferred != null) {
      this.inferredPersonCategoryIndex = inferred;
      return inferred;
    }
    return runtimePersonIndex;
  }

  private resolveRuntimePersonMaskIndex(args: {
    personLabelIndex: number | null;
    confidenceMasks: Array<{ width: number; height: number; getAsFloat32Array: () => Float32Array }>;
    stride: number;
  }): number | null {
    const { personLabelIndex, confidenceMasks, stride } = args;
    if (personLabelIndex != null) return personLabelIndex;
    if (confidenceMasks.length === 1) return 0;
    if (this.inferredPersonMaskIndex != null && confidenceMasks[this.inferredPersonMaskIndex]) {
      return this.inferredPersonMaskIndex;
    }
    if (confidenceMasks.length <= 1) return null;
    const inferred = inferPersonMaskIndexFromConfidenceMasks(confidenceMasks, stride);
    if (inferred != null) {
      this.inferredPersonMaskIndex = inferred;
      try {
        console.debug(`[FX Seg] inferred person mask index=${inferred} confidenceMasks=${confidenceMasks.length}`);
      } catch {}
    }
    return inferred;
  }

  private applyMiss(elementId: string, nowMs: number): void {
    const prev = this.states.get(elementId) ?? createDefaultState(elementId, nowMs);
    const missCount = prev.missCount + 1;
    let confidence = prev.confidence;
    let rect = prev.personRect;
    let mask = prev.mask;

    if (missCount <= FREEZE_MISS_LIMIT) {
      confidence = clamp01(confidence * 0.94);
    } else if (missCount <= SEARCH_MISS_LIMIT) {
      confidence = clamp01(confidence * 0.88);
      if (rect) {
        rect = expandRect(rect, 0.02);
      }
    } else {
      confidence = 0;
      rect = null;
      mask = null;
    }

    this.states.set(elementId, {
      elementId,
      personRect: rect,
      mask,
      confidence,
      consecutiveMisses: missCount,
      fallback: true,
      lastUpdatedMs: nowMs,
      missCount,
    });
  }

  private pickNextCandidate(
    targetElementIds: string[],
    videoMap: Map<string, HTMLVideoElement>
  ): { elementId: string; video: HTMLVideoElement } | null {
    const total = targetElementIds.length;
    if (total === 0) return null;

    for (let i = 0; i < total; i += 1) {
      const idx = (this.roundRobinCursor + i) % total;
      const elementId = targetElementIds[idx];
      const video = videoMap.get(elementId);
      if (!video) continue;
      if (video.readyState < 2) continue;
      if (video.videoWidth <= 0 || video.videoHeight <= 0) continue;
      this.roundRobinCursor = (idx + 1) % total;
      return { elementId, video };
    }

    this.roundRobinCursor = (this.roundRobinCursor + 1) % total;
    return null;
  }

  private pruneInactiveTargets(activeTargets: Set<string>): void {
    for (const elementId of this.states.keys()) {
      if (!activeTargets.has(elementId)) this.states.delete(elementId);
    }
  }

  private resolveSegmentationInput(
    video: HTMLVideoElement,
    qualityTier: FxQualityTier
  ): HTMLVideoElement | HTMLCanvasElement {
    if (typeof document === "undefined") return video;
    const sourceWidth = Math.max(1, Math.round(video.videoWidth || 1));
    const sourceHeight = Math.max(1, Math.round(video.videoHeight || 1));
    const targetLongEdge = SEGMENTATION_INPUT_LONG_EDGE_BY_TIER[qualityTier] ?? 384;
    const target = resolveTierInputDimensions(sourceWidth, sourceHeight, targetLongEdge);

    let bundle = this.inputBundleByTier.get(qualityTier);
    if (!bundle) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { alpha: false });
      if (!ctx) return video;
      bundle = { canvas, ctx, width: 0, height: 0 };
      this.inputBundleByTier.set(qualityTier, bundle);
    }

    if (bundle.width !== target.width || bundle.height !== target.height) {
      bundle.width = target.width;
      bundle.height = target.height;
      bundle.canvas.width = target.width;
      bundle.canvas.height = target.height;
    }

    try {
      bundle.ctx.drawImage(video, 0, 0, bundle.width, bundle.height);
      return bundle.canvas;
    } catch {
      return video;
    }
  }
}

function createDefaultState(elementId: string, nowMs: number): InternalBodySegmentationState {
  return {
    elementId,
    personRect: null,
    mask: null,
    confidence: 0,
    consecutiveMisses: 0,
    fallback: true,
    lastUpdatedMs: nowMs,
    missCount: 0,
  };
}

function computeRectFromConfidenceMask(args: {
  data: Float32Array;
  width: number;
  height: number;
  threshold: number;
  stride: number;
}): { rect: SegmentationRect; confidence: number } | null {
  const { data, width, height, threshold, stride } = args;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  let matchCount = 0;
  let confidenceSum = 0;

  for (let y = 0; y < height; y += stride) {
    const rowOffset = y * width;
    for (let x = 0; x < width; x += stride) {
      const score = data[rowOffset + x] ?? 0;
      if (score < threshold) continue;
      matchCount += 1;
      confidenceSum += score;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }

  if (maxX < minX || maxY < minY || matchCount === 0) return null;
  return {
    rect: {
      x: clamp01(minX / width),
      y: clamp01(minY / height),
      width: clamp((maxX - minX + 1) / width, 0.01, 1),
      height: clamp((maxY - minY + 1) / height, 0.01, 1),
    },
    confidence: clamp01(confidenceSum / matchCount),
  };
}

function computeRectFromCategoryMask(args: {
  data: Uint8Array;
  width: number;
  height: number;
  personIndex: number;
  stride: number;
}): { rect: SegmentationRect; confidence: number } | null {
  const { data, width, height, personIndex, stride } = args;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  let matchCount = 0;

  for (let y = 0; y < height; y += stride) {
    const rowOffset = y * width;
    for (let x = 0; x < width; x += stride) {
      if ((data[rowOffset + x] ?? 0) !== personIndex) continue;
      matchCount += 1;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }

  if (maxX < minX || maxY < minY || matchCount === 0) return null;
  return {
    rect: {
      x: clamp01(minX / width),
      y: clamp01(minY / height),
      width: clamp((maxX - minX + 1) / width, 0.01, 1),
      height: clamp((maxY - minY + 1) / height, 0.01, 1),
    },
    confidence: 0.8,
  };
}

function inferPersonMaskIndexFromConfidenceMasks(
  confidenceMasks: Array<{ width: number; height: number; getAsFloat32Array: () => Float32Array }>,
  stride: number
): number | null {
  let bestIndex: number | null = null;
  let bestScore = -Infinity;
  const evalStride = Math.max(2, stride);
  for (let index = 0; index < confidenceMasks.length; index += 1) {
    const mask = confidenceMasks[index];
    if (!mask) continue;
    let data: Float32Array;
    try {
      data = mask.getAsFloat32Array();
    } catch {
      continue;
    }
    const rect = computeRectFromConfidenceMask({
      data,
      width: mask.width,
      height: mask.height,
      threshold: 0.45,
      stride: evalStride,
    });
    if (!rect) continue;
    const coverage = rect.rect.width * rect.rect.height;
    if (coverage < 0.02 || coverage > 0.9) continue;
    const centerX = rect.rect.x + rect.rect.width * 0.5;
    const centerY = rect.rect.y + rect.rect.height * 0.5;
    const aspect = rect.rect.height / Math.max(0.001, rect.rect.width);
    const aspectScore = clamp01(1 - Math.abs(aspect - 1.8) / 1.8);
    const centerXScore = clamp01(1 - Math.abs(centerX - 0.5) / 0.5);
    const centerYScore = clamp01(1 - Math.abs(centerY - 0.6) / 0.6);
    const coverageScore = clamp01(1 - Math.abs(coverage - 0.32) / 0.32);
    const score =
      rect.confidence * 1.35 +
      aspectScore * 0.7 +
      centerXScore * 0.35 +
      centerYScore * 0.35 +
      coverageScore * 0.55;
    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  }
  return bestIndex;
}

function selectPreferredSegmentationCandidate(args: {
  candidates: Array<{
    source: "category" | "confidence";
    rect: SegmentationRect;
    confidence: number;
    mask: SegmentationMaskState;
  }>;
  previousCoverage: number | null;
}): {
  source: "category" | "confidence";
  rect: SegmentationRect;
  confidence: number;
  mask: SegmentationMaskState;
} {
  const { candidates, previousCoverage } = args;
  const category = candidates.find((candidate) => candidate.source === "category") ?? null;
  const confidence = candidates.find((candidate) => candidate.source === "confidence") ?? null;

  // Strong guard: if category mask blooms across most of the frame but confidence
  // mask remains portrait-shaped, prefer confidence to preserve real background blur.
  if (
    category &&
    confidence &&
    category.mask.coverage >= 0.62 &&
    confidence.mask.coverage <= category.mask.coverage - 0.12 &&
    confidence.mask.coverage >= 0.02
  ) {
    return confidence;
  }

  let best = candidates[0];
  let bestScore = -Infinity;
  const targetCoverage = previousCoverage ?? 0.34;
  for (const candidate of candidates) {
    const coverage = clamp01(candidate.mask.coverage);
    const centerX = candidate.rect.x + candidate.rect.width * 0.5;
    const centerY = candidate.rect.y + candidate.rect.height * 0.5;
    const centerScore = clamp01(1 - Math.abs(centerX - 0.5) / 0.5) * 0.2 +
      clamp01(1 - Math.abs(centerY - 0.58) / 0.58) * 0.2;
    const coverageScore = clamp01(1 - Math.abs(coverage - targetCoverage) / Math.max(0.18, targetCoverage));
    const closeupPenalty = coverage > 0.86 ? (coverage - 0.86) * 2.4 : 0;
    const score =
      candidate.confidence * 1.35 +
      coverageScore * 0.85 +
      centerScore -
      closeupPenalty +
      (candidate.source === "confidence" ? 0.06 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return best;
}

function expandRect(rect: SegmentationRect, deltaNorm: number): SegmentationRect {
  const x = clamp01(rect.x - deltaNorm);
  const y = clamp01(rect.y - deltaNorm);
  const maxW = 1 - x;
  const maxH = 1 - y;
  return {
    x,
    y,
    width: clamp(rect.width + deltaNorm * 2, 0.01, maxW),
    height: clamp(rect.height + deltaNorm * 2, 0.01, maxH),
  };
}

function dedupe(values: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

function dedupeNumbers(values: Array<number | null | undefined>): number[] {
  const out: number[] = [];
  const seen = new Set<number>();
  for (const value of values) {
    if (!Number.isFinite(value)) continue;
    const next = Math.round(Number(value));
    if (seen.has(next)) continue;
    seen.add(next);
    out.push(next);
  }
  return out;
}

function hasCategoryValue(data: Uint8Array, value: number): boolean {
  if (!Number.isFinite(value) || value < 0 || value > 255) return false;
  const target = Math.round(value);
  const stride = data.length > 640 * 640 ? 2 : 1;
  for (let i = 0; i < data.length; i += stride) {
    if ((data[i] ?? 0) === target) return true;
  }
  return false;
}

function inferCategoryForegroundIndex(data: Uint8Array): number | null {
  const counts = new Uint32Array(256);
  const stride = data.length > 640 * 640 ? 2 : 1;
  for (let i = 0; i < data.length; i += stride) {
    counts[data[i] ?? 0] += 1;
  }
  let bestIndex = 0;
  let bestCount = 0;
  for (let index = 1; index < 256; index += 1) {
    const count = counts[index];
    if (count > bestCount) {
      bestCount = count;
      bestIndex = index;
    }
  }
  if (bestCount <= 0) return null;
  return bestIndex;
}

function now(): number {
  if (typeof performance !== "undefined") return performance.now();
  return Date.now();
}

function disposeImageSegmenterResult(result: ImageSegmenterResult | null): void {
  if (!result) return;
  try {
    const confidenceMasks = Array.isArray((result as any).confidenceMasks)
      ? (result as any).confidenceMasks
      : [];
    for (const mask of confidenceMasks) {
      try {
        mask?.close?.();
      } catch {}
    }
    try {
      (result as any).categoryMask?.close?.();
    } catch {}
    try {
      (result as any).close?.();
    } catch {}
  } catch {}
}

type ResolvedTierTuning = {
  detectIntervalMs: number;
  sampleStride: number;
  frameStride: number;
  maskScale: number;
  maskEmaAlpha: number;
};

function resolveTierTuning(tier: FxQualityTier): ResolvedTierTuning {
  const fromConfig = getFxSegmentationTierTuning(tier);
  return {
    detectIntervalMs: clampInt(fromConfig.detectIntervalMs, 40, 400, DEFAULT_DETECT_INTERVAL_MS),
    sampleStride: clampInt(fromConfig.sampleStride, 1, 4, DEFAULT_SAMPLE_STRIDE),
    frameStride: clampInt(fromConfig.frameStride, 1, 6, DEFAULT_FRAME_STRIDE),
    maskScale:
      (tier === "draft"
        ? Math.min(clamp(fromConfig.maskScale, 0.25, 1), 0.65)
        : clamp(fromConfig.maskScale, 0.25, 1)) || DEFAULT_MASK_SCALE,
    maskEmaAlpha: clamp(fromConfig.maskEmaAlpha, 0.05, 1) || DEFAULT_MASK_EMA_ALPHA,
  };
}

function buildMaskFromConfidenceMask(args: {
  data: Float32Array;
  width: number;
  height: number;
  alphaThreshold: number;
  alphaSoftness: number;
  sampleStride: number;
  maskScale: number;
  emaAlpha: number;
  previousMask: SegmentationMaskState | null;
  generatedAtMs: number;
}): SegmentationMaskState {
  const {
    data,
    width,
    height,
    alphaThreshold,
    alphaSoftness,
    sampleStride,
    maskScale,
    emaAlpha,
    previousMask,
    generatedAtMs,
  } = args;
  const targetWidth = resolveScaledDimension(width, maskScale);
  const targetHeight = resolveScaledDimension(height, maskScale);
  const prevReusable = previousMask?.width === targetWidth && previousMask?.height === targetHeight
    ? previousMask
    : null;
  const alpha = prevReusable?.alpha ?? new Float32Array(targetWidth * targetHeight);
  const blend = clamp01(emaAlpha);
  const safeThreshold = clamp(alphaThreshold, 0.35, 0.85);
  const safeSoftness = clamp(alphaSoftness, 0.02, 0.45);
  const softStart = safeThreshold - safeSoftness * 0.5;
  const softRange = Math.max(0.001, safeSoftness);

  let confidenceSum = 0;
  let coverageCount = 0;
  for (let y = 0; y < targetHeight; y += 1) {
    const sourceY = quantizeSourceIndex(y, targetHeight, height, sampleStride);
    const sourceOffset = sourceY * width;
    const targetOffset = y * targetWidth;
    for (let x = 0; x < targetWidth; x += 1) {
      const sourceX = quantizeSourceIndex(x, targetWidth, width, sampleStride);
      const rawConfidence = clamp01(data[sourceOffset + sourceX] ?? 0);
      const sourceAlpha = clamp01((rawConfidence - softStart) / softRange);
      const index = targetOffset + x;
      const prevAlpha = prevReusable ? alpha[index] : sourceAlpha;
      const nextAlpha = prevReusable ? prevAlpha + (sourceAlpha - prevAlpha) * blend : sourceAlpha;
      alpha[index] = nextAlpha;
      confidenceSum += nextAlpha;
      if (nextAlpha >= 0.45) coverageCount += 1;
    }
  }

  const area = Math.max(1, targetWidth * targetHeight);
  return {
    width: targetWidth,
    height: targetHeight,
    alpha,
    meanConfidence: clamp01(confidenceSum / area),
    coverage: clamp01(coverageCount / area),
    scale: Math.min(targetWidth / Math.max(1, width), targetHeight / Math.max(1, height)),
    generatedAtMs,
  };
}

function buildMaskFromCategoryMask(args: {
  data: Uint8Array;
  width: number;
  height: number;
  personIndex: number;
  sampleStride: number;
  maskScale: number;
  emaAlpha: number;
  previousMask: SegmentationMaskState | null;
  generatedAtMs: number;
}): SegmentationMaskState {
  const { data, width, height, personIndex, sampleStride, maskScale, emaAlpha, previousMask, generatedAtMs } = args;
  const targetWidth = resolveScaledDimension(width, maskScale);
  const targetHeight = resolveScaledDimension(height, maskScale);
  const prevReusable = previousMask?.width === targetWidth && previousMask?.height === targetHeight
    ? previousMask
    : null;
  const alpha = prevReusable?.alpha ?? new Float32Array(targetWidth * targetHeight);
  const blend = clamp01(emaAlpha);

  let confidenceSum = 0;
  let coverageCount = 0;
  for (let y = 0; y < targetHeight; y += 1) {
    const sourceY = quantizeSourceIndex(y, targetHeight, height, sampleStride);
    const sourceOffset = sourceY * width;
    const targetOffset = y * targetWidth;
    for (let x = 0; x < targetWidth; x += 1) {
      const sourceX = quantizeSourceIndex(x, targetWidth, width, sampleStride);
      const sourceAlpha = (data[sourceOffset + sourceX] ?? 0) === personIndex ? 1 : 0;
      const index = targetOffset + x;
      const prevAlpha = prevReusable ? alpha[index] : sourceAlpha;
      const nextAlpha = prevReusable ? prevAlpha + (sourceAlpha - prevAlpha) * blend : sourceAlpha;
      alpha[index] = nextAlpha;
      confidenceSum += nextAlpha;
      if (nextAlpha >= 0.5) coverageCount += 1;
    }
  }

  const area = Math.max(1, targetWidth * targetHeight);
  return {
    width: targetWidth,
    height: targetHeight,
    alpha,
    meanConfidence: clamp01(confidenceSum / area),
    coverage: clamp01(coverageCount / area),
    scale: Math.min(targetWidth / Math.max(1, width), targetHeight / Math.max(1, height)),
    generatedAtMs,
  };
}

function resolveScaledDimension(size: number, scale: number): number {
  const safeSize = Math.max(1, Math.round(size));
  const safeScale = clamp(scale, 0.25, 1);
  return Math.max(1, Math.min(safeSize, Math.round(safeSize * safeScale)));
}

function resolveTierInputDimensions(
  sourceWidth: number,
  sourceHeight: number,
  longEdge: number
): { width: number; height: number } {
  const safeWidth = Math.max(1, sourceWidth);
  const safeHeight = Math.max(1, sourceHeight);
  const safeLongEdge = Math.max(64, Math.round(longEdge));
  if (safeWidth >= safeHeight) {
    return {
      width: safeLongEdge,
      height: Math.max(64, Math.round((safeHeight / safeWidth) * safeLongEdge)),
    };
  }
  return {
    width: Math.max(64, Math.round((safeWidth / safeHeight) * safeLongEdge)),
    height: safeLongEdge,
  };
}

function quantizeSourceIndex(
  targetIndex: number,
  targetSize: number,
  sourceSize: number,
  stride: number
): number {
  const safeSourceSize = Math.max(1, sourceSize);
  const raw = Math.min(
    safeSourceSize - 1,
    Math.floor(((targetIndex + 0.5) * safeSourceSize) / Math.max(1, targetSize))
  );
  const safeStride = Math.max(1, stride);
  if (safeStride === 1) return raw;
  return Math.max(0, Math.min(safeSourceSize - 1, Math.floor(raw / safeStride) * safeStride));
}

function clampInt(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(min, Math.min(max, Math.round(value)));
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

function lerp(from: number, to: number, alpha: number): number {
  return from + (to - from) * clamp01(alpha);
}
